// src/features/schedule/components/MomentumCard.tsx
import React, { useMemo, useState, useCallback } from 'react';
import { useCurrentTime } from '../../../hooks/useCurrentTime';
import { Sparkles, ChevronRight } from '../../../components/common/icons';
import QuickCreateModal from './QuickCreateModal';
import { eventBus, EVENTS } from '../../../utils/eventBus';
import { Task } from '../../../types/task';
import { ScheduleItem } from '../../../types/scheduleItem';
import { DayPhase } from '../../../hooks/useDayPhase';
import { formatRelativeDate, formatDateToString } from '../../../utils/dateUtils';
import { formatDurationFromMinutes } from '../../../utils/formatters';
import { useScheduleGeneratorTrigger } from '../../scheduleGenerator';

type MomentumScenario = 'B_C' | 'D_E' | 'F';

interface MomentumCardProps {
  nextItem: ScheduleItem | null;
  hasCompletedWork: boolean;
  dayPhase: DayPhase;
  topOpenTasks?: Task[];
  isLoading?: boolean;
  onRefresh?: () => void;
  onGetAISuggestion?: () => void;
}

// 共用：task 列表區塊
function TaskList({ tasks, onTaskClick }: { tasks: Task[]; onTaskClick: (task: Task) => void }) {
  return (
    <div className="flex flex-col gap-1">
      {tasks.slice(0, 3).map((task) => {
        const dateStr = task.targetDate || task.plan;
        const dateLabel = dateStr ? formatRelativeDate(dateStr) : null;
        const isOverdue = dateLabel?.startsWith('Overdue');
        const isToday = dateLabel === 'Today';

        return (
          <button
            key={task.id}
            className="w-full flex items-center justify-between py-2.5 px-3 rounded-lg border border-gray-100 hover:border-sky-200 hover:bg-sky-50 transition-colors text-left group"
            onClick={() => onTaskClick(task)}
            aria-label={`Timebox "${task.title}"`}
          >
            <span className="text-sm text-gray-700 font-medium truncate flex-1 mr-3 group-hover:text-sky-700 transition-colors">
              {task.title}
            </span>
            <div className="flex items-center gap-2 flex-shrink-0">
              {dateLabel && (
                <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded-md ${
                  isOverdue
                    ? 'bg-red-50 text-red-500'
                    : isToday
                      ? 'bg-sky-50 text-sky-500'
                      : 'bg-gray-100 text-gray-400'
                }`}>
                  {dateLabel}
                </span>
              )}
              <ChevronRight size={14} className="text-gray-300 group-hover:text-sky-400 transition-colors" />
            </div>
          </button>
        );
      })}
    </div>
  );
}

// 共用：右欄 CTA 區塊（豎向）
function CtaColumn({
  sectionLabel,
  ctaLabel,
  ctaAriaLabel,
  onCta,
  onQuickCreate,
}: {
  sectionLabel: string;
  ctaLabel: string;
  ctaAriaLabel: string;
  onCta: () => void;
  onQuickCreate: () => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
          {sectionLabel}
        </p>
        <button
          className="btn-primary w-full"
          onClick={onCta}
          aria-label={ctaAriaLabel}
        >
          <Sparkles size={14} />
          {ctaLabel}
        </button>
      </div>
      <div className="border-t border-gray-100" />
      <button
        className="w-full text-sm text-gray-500 hover:text-sky-600 font-medium py-2 px-3 rounded-lg hover:bg-sky-50 transition-colors text-left"
        onClick={onQuickCreate}
      >
        + Quick Create
      </button>
    </div>
  );
}

const MomentumCard: React.FC<MomentumCardProps> = ({
  nextItem,
  hasCompletedWork,
  dayPhase,
  topOpenTasks = [],
  isLoading = false,
  onRefresh,
  onGetAISuggestion,
}) => {
  const [quickModalTask, setQuickModalTask] = useState<{ title?: string; taskId?: string } | null>(null);
  const { triggerAIPlan } = useScheduleGeneratorTrigger();
  const currentTime = useCurrentTime();

  const scenario: MomentumScenario = useMemo(() => {
    if (nextItem) return 'B_C';
    if (!hasCompletedWork) return 'D_E';
    return 'F';
  }, [nextItem, hasCompletedWork]);

  const freeMinutes = useMemo(() => {
    if (!nextItem) return null;
    const freeMs = new Date(nextItem.startTime).getTime() - currentTime.getTime();
    if (isNaN(freeMs)) return null;
    return Math.max(0, Math.floor(freeMs / 60000));
  }, [nextItem, currentTime]);

  const openQuickModal = useCallback((title?: string, taskId?: string) => {
    setQuickModalTask({ title, taskId });
    eventBus.publish(EVENTS.UI.TODAY_QUICK_CREATE_OPENED);
  }, []);

  const handleCloseQuickModal = useCallback(() => {
    setQuickModalTask(null);
  }, []);

  if (isLoading) {
    return (
      <div className="relative overflow-hidden p-5 md:p-6 rounded-xl md:rounded-1.5xl bg-white border border-gray-200 animate-pulse">
        <div className="space-y-3">
          <div className="h-8 w-36 bg-gray-200 rounded" />
          <div className="h-4 w-24 bg-gray-100 rounded" />
          <div className="h-10 w-32 bg-gray-200 rounded mt-4" />
        </div>
      </div>
    );
  }

  // 共用佈局：標題區 + 任務列表（左）+ CTA 右欄，容器寬時橫向，窄時豎向
  const hasTasks = topOpenTasks.length > 0;

  const renderTwoColumnLayout = (
    taskListLabel: string,
    ctaColumn: React.ReactNode,
  ) => (
    <div className="flex flex-col @[700px]:flex-row @[700px]:gap-8">
      {hasTasks && (
        <div className="flex-1 mb-4 @[700px]:mb-0">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
            {taskListLabel}
          </p>
          <TaskList tasks={topOpenTasks} onTaskClick={(task) => openQuickModal(task.title, task.id)} />
        </div>
      )}
      {hasTasks && <div className="hidden @[700px]:block w-px bg-gray-100 flex-shrink-0" />}
      <div className={hasTasks ? 'w-full @[700px]:w-52 flex-shrink-0' : 'w-full'}>
        {ctaColumn}
      </div>
    </div>
  );

  const renderScenarioContent = () => {
    // ── 場景 B/C：有 nextItem，現在空閒 ──────────────────
    if (scenario === 'B_C' && nextItem) {
      const showFreeTime = freeMinutes !== null && freeMinutes > 0;
      return (
        <>
          <div role="status" aria-live="polite">
            <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-1">
              {showFreeTime ? `Free for ${formatDurationFromMinutes(freeMinutes!)}` : 'Free now'}
            </h2>
            <p className="text-gray-500 text-sm mb-6">before {nextItem.title}</p>
          </div>
          {renderTwoColumnLayout(
            'Pick one to move forward',
            <CtaColumn
              sectionLabel={hasTasks ? 'Or get a suggestion' : 'Get a suggestion'}
              ctaLabel="What's next?"
              ctaAriaLabel="Get a suggestion for what to do next"
              onCta={() => onGetAISuggestion?.()}
              onQuickCreate={() => openQuickModal()}
            />,
          )}
        </>
      );
    }

    // ── 場景 D/E：nextItem 不存在 + hasCompletedWork = false ──
    if (scenario === 'D_E') {
      const isMorning = dayPhase === 'morning';
      const title = isMorning ? 'A fresh morning' : 'Still time to make today count';
      const subtitle = isMorning ? 'Set the tone for today.' : 'Start with one focused block.';

      const handleDECta = () => {
        if (isMorning) {
          triggerAIPlan(
            { source: 'today-current', date: formatDateToString(new Date()) },
            { onDataRefresh: onRefresh }
          );
        } else {
          onGetAISuggestion?.();
        }
      };

      return (
        <>
          <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-1" role="status" aria-live="polite">
            {title}
          </h2>
          <p className="text-gray-500 text-sm mb-6">{subtitle}</p>
          {renderTwoColumnLayout(
            'On your list',
            <CtaColumn
              sectionLabel={isMorning
                ? (hasTasks ? 'Or plan your day' : 'Plan your day')
                : (hasTasks ? 'Or get a suggestion' : 'Get a suggestion')}
              ctaLabel={isMorning ? 'Plan now' : "What's next?"}
              ctaAriaLabel={isMorning ? 'Plan my day' : 'Get a suggestion for what to do next'}
              onCta={handleDECta}
              onQuickCreate={() => openQuickModal()}
            />,
          )}
        </>
      );
    }

    // ── 場景 F：nextItem 不存在 + hasCompletedWork = true ──
    return (
      <>
        <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-1" role="status" aria-live="polite">
          You've cleared your schedule
        </h2>
        <p className="text-gray-500 text-sm mb-6">Here's what's waiting.</p>
        {renderTwoColumnLayout(
          'On your list',
          <CtaColumn
            sectionLabel={hasTasks ? 'Or get a suggestion' : 'Get a suggestion'}
            ctaLabel="What's next?"
            ctaAriaLabel="Get a suggestion for what to do next"
            onCta={() => onGetAISuggestion?.()}
            onQuickCreate={() => openQuickModal()}
          />,
        )}
      </>
    );
  };

  return (
    <div className="@container relative overflow-hidden p-5 md:p-8 rounded-xl md:rounded-1.5xl bg-white border border-gray-200 z-content">
      {renderScenarioContent()}
      <QuickCreateModal
        isOpen={quickModalTask !== null}
        onClose={handleCloseQuickModal}
        onSuccess={() => { if (onRefresh) onRefresh(); }}
        initialData={quickModalTask ?? undefined}
        maxDurationMinutes={freeMinutes ?? undefined}
      />
    </div>
  );
};

export default MomentumCard;
